import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { data, dinheiro } from '../../nucleo/formato';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { Barra, BotaoExportar, Kpi, LinhaEscopo, Rolagem, TD, TH, usePeriodo, Valor } from './comum';
import type { Extrato as ExtratoApi } from './tipos';
import { rotuloCategoria } from '../../nucleo/rotulos';

/** R5 · Extrato consolidado: uma seção por conta com saldo corrido (fecha com o saldo do Financeiro) e o total por moeda. */
export function Extrato() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const params = { de: periodo.de, ate: periodo.ate };
  const q = useQuery({ queryKey: ['/relatorios/extrato', params], queryFn: () => get<ExtratoApi>('/relatorios/extrato', params), placeholderData: keepPreviousData });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <BotaoExportar relatorio="extrato" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <LinhaEscopo escopo={d.escopo} />
            {d.contas.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('extrato.vazioTitulo')} texto={t('extrato.vazioTexto')} />
              </Cartao>
            ) : (
              <>
                {d.totais.map((tm) => (
                  <section key={tm.moeda} aria-label={t('extrato.totalMoeda', { moeda: tm.moeda })} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Kpi rotulo={`${t('extrato.saldoAnterior')} (${tm.moeda})`} valor={dinheiro(tm.saldo_anterior_centavos, tm.moeda)} />
                    <Kpi rotulo={t('extrato.entradas')} cor="text-verde" valor={dinheiro(tm.entradas_centavos, tm.moeda)} />
                    <Kpi rotulo={t('extrato.saidas')} cor="text-perigo" valor={dinheiro(tm.saidas_centavos, tm.moeda)} />
                    <Kpi rotulo={t('extrato.totalMoeda', { moeda: tm.moeda })} cor={tm.saldo_final_centavos < 0 ? 'text-perigo' : 'text-tinta-1'} valor={dinheiro(tm.saldo_final_centavos, tm.moeda)} />
                  </section>
                ))}
                {d.contas.map((c) => (
                  <Cartao
                    key={c.conta_id}
                    titulo={
                      <span className="inline-flex flex-wrap items-center gap-2">
                        {c.conta_nome}
                        {!c.ativa && <Selo>{t('extrato.inativa')}</Selo>}
                      </span>
                    }
                    acao={<Valor centavos={c.saldo_final_centavos} moeda={c.moeda} className="text-sm font-semibold" />}
                    semPadding
                  >
                    {/* Review r16 visual B7: uma região rolável por conta, cada uma com o nome da conta (landmark-unique). */}
                    <Rolagem rotulo={`${t('comum.tabela')}: ${c.conta_nome}`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-borda text-left text-tinta-3">
                            <th scope="col" className={TH}>{t('extrato.data')}</th>
                            <th scope="col" className={TH}>{t('extrato.descricao')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('extrato.valor')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('extrato.saldo')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-borda bg-sup-2">
                            <td className={`${TD} numero whitespace-nowrap text-tinta-2`}>{data(d.de)}</td>
                            <th scope="row" className={`${TD} text-left font-medium`}>{t('extrato.saldoAnterior')}</th>
                            <td className={TD} />
                            <td className={`${TD} text-right font-medium`}>
                              <Valor centavos={c.saldo_anterior_centavos} moeda={c.moeda} />
                            </td>
                          </tr>
                          {c.linhas.length === 0 && (
                            <tr className="border-b border-borda">
                              <td colSpan={4} className={`${TD} text-tinta-3`}>{t('extrato.semLinhas')}</td>
                            </tr>
                          )}
                          {c.linhas.map((l) => (
                            <tr key={l.id} className="border-b border-borda">
                              <td className={`${TD} numero whitespace-nowrap text-tinta-2`}>{data(l.data_caixa)}</td>
                              <td className={`${TD} max-w-0 min-w-40`}>
                                <TextoCortado className="block">{descricaoGerada(l.descricao)}</TextoCortado>
                                <TextoCortado className="block text-xs text-tinta-3">
                                  {[
                                    l.tipo === 'transferencia' && l.outra_conta_nome ? t(l.efeito_centavos < 0 ? 'extrato.transferenciaPara' : 'extrato.transferenciaDe', { conta: l.outra_conta_nome }) : t(`extrato.tipo.${l.tipo}`),
                                    rotuloCategoria(l.categoria_nome),
                                    l.estorno_de ? t('extrato.estorno') : l.estornado_por ? t('extrato.estornado') : null,
                                    l.conciliado ? t('extrato.conciliado') : null,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                </TextoCortado>
                              </td>
                              <td className={`${TD} text-right`}>
                                <Valor centavos={l.efeito_centavos} moeda={c.moeda} />
                              </td>
                              <td className={`${TD} text-right font-medium`}>
                                <Valor centavos={l.saldo_centavos} moeda={c.moeda} />
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-sup-2 font-semibold">
                            <td className={`${TD} numero whitespace-nowrap`}>{data(d.ate)}</td>
                            <th scope="row" className={`${TD} text-left`}>{t('extrato.saldoFinal')}</th>
                            <td className={TD} />
                            <td className={`${TD} text-right`}>
                              <Valor centavos={c.saldo_final_centavos} moeda={c.moeda} />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Rolagem>
                    {c.truncado && <p className="border-t border-borda px-4 py-2 text-xs text-tinta-3">{t('extrato.truncado', { n: c.linhas.length, total: c.total_linhas })}</p>}
                  </Cartao>
                ))}
                <p className="text-xs text-tinta-3">{t('extrato.fecha')}</p>
              </>
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
